import { defineStore } from 'pinia'
import { ElMessage, ElMessageBox, ElNotification } from 'element-plus'
import router from '@/router'
import {
  getExportPassword,
  listGeneratingExportTasks,
  listExportTasks,
} from '@/api/exportRecords'
import {
  EXPORT_AES_TIP,
  EXPORT_POLL_INTERVAL,
  EXPORT_RECORDS_PATH,
  type ExportTask,
} from '@/types/export'

let pollingTimer: ReturnType<typeof setInterval> | undefined
let pollingInFlight = false
let notificationInFlight = false
let pollingGeneration = 0

function unwrapData(response: any) {
  return response?.data ?? response
}

function normalizeDataType(dataType: unknown) {
  const value = String(dataType ?? '').toUpperCase()
  if (['STUDENT', '学员', '学员信息'].includes(value)) return 'STUDENT'
  if (['TEACHER', '教师', '老师', '教师信息', '老师信息'].includes(value)) return 'TEACHER'
  return value
}

function normalizeStatus(status: unknown) {
  const value = String(status ?? '').toUpperCase()
  return {
    '生成中': 'GENERATING',
    '已完成': 'COMPLETED',
    '已过期': 'EXPIRED',
    '失败': 'FAILED',
  }[value] ?? value
}

function normalizeTask(raw: any): ExportTask {
  const task = unwrapData(raw) || {}
  return {
    ...task,
    id: task.id ?? task.taskId,
    taskId: task.taskId ?? task.id,
    count: task.count ?? task.total,
    dataType: normalizeDataType(task.dataType ?? task.type),
    status: normalizeStatus(task.status ?? 'GENERATING'),
  } as ExportTask
}

function getList(response: any): any[] {
  const data = unwrapData(response)
  return data?.records ?? data?.list ?? (Array.isArray(data) ? data : [])
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value)
  ElMessage.success('解压密码已复制')
}

export const useExportTasksStore = defineStore('exportTasks', {
  state: () => ({
    activeTasks: [] as ExportTask[],
    completedQueue: [] as ExportTask[],
    notifiedTaskIds: [] as string[],
    initialized: false,
  }),
  actions: {
    async restoreGeneratingTasks() {
      if (this.initialized) return
      this.initialized = true
      try {
        const response = await listGeneratingExportTasks(true)
        this.activeTasks = getList(response).map(normalizeTask)
        if (this.activeTasks.length) this.startPolling()
      } catch {
        // 恢复任务失败不阻塞后台正常使用，进入记录页后仍可查看任务状态。
        this.initialized = false
      }
    },
    trackTask(rawTask: any) {
      const task = normalizeTask(rawTask)
      if (!task.id) return
      const index = this.activeTasks.findIndex(item => String(item.id) === String(task.id))
      if (index >= 0) this.activeTasks[index] = task
      else this.activeTasks.push(task)
      this.startPolling()
    },
    startPolling() {
      if (pollingTimer || !this.activeTasks.length) return
      pollingTimer = setInterval(() => this.pollTasks(), EXPORT_POLL_INTERVAL)
      this.pollTasks()
    },
    stopPolling() {
      if (pollingTimer) clearInterval(pollingTimer)
      pollingTimer = undefined
    },
    async pollTasks() {
      if (pollingInFlight || !this.activeTasks.length) {
        if (!this.activeTasks.length) this.stopPolling()
        return
      }
      pollingInFlight = true
      const generation = pollingGeneration
      const snapshot = [...this.activeTasks]
      try {
        const response = await listExportTasks({ pageNum: 1, pageSize: 100 }, true)
        if (generation !== pollingGeneration) return
        const tasksById = new Map(
          getList(response).map(normalizeTask).map(task => [String(task.id), task]),
        )
        snapshot.forEach(activeTask => {
          const id = String(activeTask.id)
          const task = tasksById.get(id)
          if (!task) return
          const activeIndex = this.activeTasks.findIndex(item => String(item.id) === id)
          if (task.status === 'GENERATING') {
            if (activeIndex >= 0) this.activeTasks[activeIndex] = task
            return
          }
          if (activeIndex >= 0) this.activeTasks.splice(activeIndex, 1)
          if (task.status === 'COMPLETED' && !this.notifiedTaskIds.includes(id)) {
            this.notifiedTaskIds.push(id)
            this.completedQueue.push(task)
          } else if (task.status === 'FAILED') {
            ElNotification.error({
              title: '导出失败',
              message: task.failureReason || '导出任务执行失败，请稍后重试。',
            })
          }
        })
        this.showNextCompletedTask()
      } finally {
        pollingInFlight = false
        if (!this.activeTasks.length) this.stopPolling()
      }
    },
    async showNextCompletedTask() {
      if (notificationInFlight || !this.completedQueue.length) return
      notificationInFlight = true
      const task = this.completedQueue.shift()!
      let password = ''
      try {
        const response = await getExportPassword(task.id)
        const data = unwrapData(response)
        password = String(data?.unzipPassword ?? '')
      } catch {
        // 无密码查看权限时仍提示任务完成，可在导出记录页由有权限人员处理。
      }
      const passwordHtml = password
        ? `<p style="margin:8px 0">解压密码：<strong>${password}</strong></p>`
        : '<p style="margin:8px 0">请前往导出记录页查看解压密码。</p>'
      try {
        await ElMessageBox.confirm(
          `<p>导出文件已生成。</p>${passwordHtml}<p style="margin:8px 0 0;color:#606266">${EXPORT_AES_TIP}</p>`,
          '导出成功',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: password ? '复制密码' : '前往导出记录',
            cancelButtonText: '前往导出记录',
            distinguishCancelAndClose: true,
            type: 'success',
          },
        )
        if (password) await copyText(password)
        else await router.push(EXPORT_RECORDS_PATH)
      } catch (action) {
        if (action === 'cancel') await router.push(EXPORT_RECORDS_PATH)
      } finally {
        notificationInFlight = false
        this.showNextCompletedTask()
      }
    },
    reset() {
      pollingGeneration += 1
      pollingInFlight = false
      notificationInFlight = false
      this.stopPolling()
      this.activeTasks = []
      this.completedQueue = []
      this.notifiedTaskIds = []
      this.initialized = false
    },
  },
})

export default useExportTasksStore
